/**
 * Analytics bridge. Fires each event to every analytics platform present on the
 * page — GA4 (gtag), Google Tag Manager (dataLayer) and Vercel Analytics (va).
 * Only non-personal parameters (page, placement, tool slug, destination) are sent.
 */

export type AnalyticsEvent =
  // Marketing funnel
  | 'primary_cta_click'
  | 'sign_in_click'
  | 'enterprise_demo_click'
  | 'sample_memo_open'
  | 'sample_memo_download'
  | 'tool_card_click'
  // Video
  | 'hero_video_play'
  | 'video_progress_25'
  | 'video_progress_50'
  | 'video_progress_75'
  | 'video_progress_100'
  // Tool pages
  | 'tool_page_view'
  | 'tool_cta_click'
  | 'tool_demo_play'
  | 'related_tool_click'

/**
 * Funnel events that must be fired by the APPLICATION (app.dgpt.io), not this site.
 * Use the same GA4 property so marketing clicks and product outcomes can be joined.
 */
export const APP_FUNNEL_EVENTS = ['sign_up', 'first_assessment_started', 'first_assessment_completed'] as const

type AnalyticsWindow = Window & {
  gtag?: (...args: unknown[]) => void
  dataLayer?: unknown[]
  va?: (event: 'event', payload: { name: string; data?: Record<string, unknown> }) => void
}

/** Google Analytics 4 measurement ID. */
export const GA_MEASUREMENT_ID = 'G-JM9VK2QN0M'

/** Google Tag Manager container ID. */
export const GTM_CONTAINER_ID = 'GTM-KXH74DQS'

/**
 * Installs Google Tag Manager — equivalent to the official <head> snippet.
 * Runs before React renders. The <noscript> iframe fallback is added to <body>
 * for parity with the official snippet (it only matters when JavaScript is off).
 */
export function initGoogleTagManager() {
  if (typeof window === 'undefined') return
  const w = window as AnalyticsWindow
  if (document.querySelector(`script[src*="gtm.js?id=${GTM_CONTAINER_ID}"]`)) return

  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`
  const first = document.getElementsByTagName('script')[0]
  if (first?.parentNode) first.parentNode.insertBefore(script, first)
  else document.head.appendChild(script)

  if (document.body && !document.getElementById('gtm-noscript')) {
    const noscript = document.createElement('noscript')
    noscript.id = 'gtm-noscript'
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
    document.body.insertBefore(noscript, document.body.firstChild)
  }
}

/** Installs the Google tag (gtag.js) once at startup. */
export function initGoogleAnalytics() {
  if (typeof window === 'undefined') return
  const w = window as AnalyticsWindow
  if (document.querySelector(`script[src*="gtag/js?id=${GA_MEASUREMENT_ID}"]`)) return

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  w.dataLayer = w.dataLayer || []
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments)
  }
  w.gtag('js', new Date())
  w.gtag('config', GA_MEASUREMENT_ID)
}

export function trackEvent(name: AnalyticsEvent, params: Record<string, unknown> = {}) {
  if (typeof window === 'undefined') return
  const w = window as AnalyticsWindow
  const payload = { ...params, page_path: window.location.pathname }

  try {
    w.gtag?.('event', name, payload)
    w.dataLayer?.push({ event: name, ...payload })
    w.va?.('event', { name, data: payload })
  } catch {
    // Analytics must never break the page.
  }
}
